'use client';

import { useEffect } from 'react';

import * as Sentry from '@sentry/nextjs';
import { useSearchParams } from 'next/navigation';

import { Unauthorized } from '@app/(legacy)/auth/_components/Unauthorized';
import { signInAction } from '@lib/actions';
import { reloadIfStaleServerAction } from '@lib/errors/reloadIfStaleServerAction';

import type { FC } from 'react';

const AuthRedirectPage: FC = () => {
  const searchParams = useSearchParams();
  const error = searchParams.get('error');
  const code = searchParams.get('code');
  const callbackUrl = searchParams.get('callbackUrl');
  const isManualSignOut = searchParams.has('manualSignOut');

  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'Auth page rendered',
    level: 'info',
    data: {
      callbackUrl,
      error,
      code,
      isManualSignOut,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    },
  });

  // Called imperatively (not via form submission) because this is an auto-sign-in
  // on page load — there is no user click to trigger a form action.
  useEffect(() => {
    if (!isManualSignOut && error == null) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Auto sign-in triggered from useEffect',
        level: 'info',
        data: { callbackUrl },
      });

      const promise =
        callbackUrl != null && callbackUrl.length > 0
          ? (() => {
              const formData = new FormData();
              formData.set('callbackUrl', callbackUrl);
              return signInAction(formData);
            })()
          : signInAction();

      promise?.catch((err: unknown) => {
        if (reloadIfStaleServerAction(err)) return;

        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Auto sign-in rejected',
          level: 'error',
          data: {
            errorType: err instanceof Error ? err.name : typeof err,
            errorMessage: err instanceof Error ? err.message : String(err),
          },
        });
      });
    }
  }, [isManualSignOut, error, callbackUrl]);

  const message =
    error != null
      ? `Sign-in failed: ${error}${code != null ? ` (${code})` : ''}. Please try again.`
      : 'Please sign in to continue';

  return (
    <Unauthorized
      message={message}
      buttonLabel="Sign in"
      action={signInAction}
      callbackUrl={callbackUrl ?? undefined}
    />
  );
};

export default AuthRedirectPage;
